/**
 * 결재선 관리 API (v2.100.0) — 시스템 관리 › 결재선 관리 (admin 전용).
 */

interface ApiResponse<T> {
  ok: boolean;
  data: T;
  error?: { message: string };
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error?.message ?? '요청 실패');
  return json.data;
}

export interface ApprovalActorUser {
  userId: string;
  name: string;
  department: string | null;
  title: string | null;
}

export interface ApprovalActors {
  expense: {
    deptHeads: Record<string, string>;
    supportIds: string[];
    ceoUserId: string;
  };
  salesReport: {
    deptHeadUserId: string;
    ceoUserId: string;
    salesSupportUserId: string;
  };
  users: ApprovalActorUser[];
}

export function getApprovalActors(): Promise<ApprovalActors> {
  return call('/api/admin/approval/actors');
}

export function saveApprovalActors(body: Omit<ApprovalActors, 'users'>): Promise<{ saved: boolean }> {
  return call('/api/admin/approval/actors', { method: 'PUT', body: JSON.stringify(body) });
}

export interface DelegateRow {
  id: number;
  principalUserId: string;
  principalName: string;
  delegateUserId: string;
  delegateName: string;
  startDate: string;
  endDate: string;
  memo: string | null;
  createdBy: string;
  createdAt: string;
  active: boolean;
}

export function listDelegates(): Promise<{ rows: DelegateRow[] }> {
  return call('/api/admin/approval/delegates');
}

export function createDelegate(body: {
  principalUserId: string;
  delegateUserId: string;
  startDate: string;
  endDate: string;
  memo?: string;
}): Promise<{ id: number }> {
  return call('/api/admin/approval/delegates', { method: 'POST', body: JSON.stringify(body) });
}

export function deleteDelegate(id: number): Promise<{ deleted: boolean }> {
  return call(`/api/admin/approval/delegates/${id}`, { method: 'DELETE' });
}

/**
 * 기술지원팀 월간보고서 상신 대상자 명단 (v2.113.0).
 *
 * 기술지원 포털이 **직원 명의로** 결재를 올리는 통로라, 여기 등록된 계정 명의로만
 * 상신할 수 있다. 키가 유출돼도 사칭 범위가 이 명단 안으로 좁혀진다.
 * ⚠명단에서 빼도 이미 올라간 문서는 그대로 결재가 돈다 — 앞으로의 상신만 막힌다.
 */
export interface TspAuthorRow {
  userId: string;
  name: string;
  title: string | null;
  department: string | null;
  note: string | null;
  createdAt: string;
  isActive: boolean;
}

export function listTspAuthors(): Promise<{ rows: TspAuthorRow[] }> {
  return call('/api/admin/approval/tsp-authors');
}

export function addTspAuthor(body: { userId: string; note?: string }): Promise<{ userId: string }> {
  return call('/api/admin/approval/tsp-authors', { method: 'POST', body: JSON.stringify(body) });
}

export function deleteTspAuthor(userId: string): Promise<{ deleted: boolean }> {
  return call(`/api/admin/approval/tsp-authors/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}
